/**
 * The signed-in app shell — sidebar nav + topbar with credit indicator.
 *
 * Sidebar links: My documents · Credits · Invites · Settings · (Admin if
 * applicable). Topbar shows the user identity + a logout link.
 *
 * Responsive: below 1200px the sidebar collapses into a hamburger-triggered
 * drawer.
 */

import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Wordmark } from '../brand/Wordmark';
import { useSession } from '../../hooks/useSession';
import { useMediaQuery } from '../../hooks/useMediaQuery';

export interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { user, isAdmin, isDemo, logout } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const isCompact = useMediaQuery('(max-width: 1199px)');
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isCompact) setDrawerOpen(false);
  }, [isCompact]);

  const asideBase: CSSProperties = {
    background: 'var(--vellum)',
    borderRight: '1px solid var(--border)',
    padding: '24px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  };
  const asideCompact: CSSProperties = {
    ...asideBase,
    position: 'fixed',
    left: 0,
    top: 0,
    height: '100vh',
    width: 260,
    zIndex: 30,
    transform: drawerOpen ? 'translateX(0)' : 'translateX(-100%)',
    transition: 'transform 200ms ease',
    boxShadow: drawerOpen ? '0 0 24px rgba(0,0,0,0.18)' : 'none',
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: isCompact ? '1fr' : '240px 1fr',
        background: 'var(--paper)',
      }}
    >
      <aside style={isCompact ? asideCompact : asideBase} aria-hidden={isCompact && !drawerOpen}>
        <div style={{ padding: '0 8px 16px' }}>
          <Wordmark size={19} />
        </div>
        <SidebarLink to="/my-documents">My documents</SidebarLink>
        <SidebarLink to="/credits">Credits</SidebarLink>
        <SidebarLink to="/settings">Settings</SidebarLink>
        {isAdmin && (
          <>
            <div style={{ height: 16 }} />
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--fg-3)',
                padding: '8px 8px 4px',
              }}
            >
              Admin
            </div>
            <SidebarLink to="/admin/users">Users</SidebarLink>
            <SidebarLink to="/admin/waitlist">Waitlist</SidebarLink>
            <SidebarLink to="/admin/demo-links">Demo links</SidebarLink>
            <SidebarLink to="/admin/metrics">Token metrics</SidebarLink>
          </>
        )}
      </aside>

      {isCompact && drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.32)',
            zIndex: 20,
          }}
          aria-hidden="true"
        />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <header
          style={{
            height: 56,
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: isCompact ? '0 16px' : '0 24px',
            background: 'rgba(237,237,242,0.85)',
            backdropFilter: 'blur(12px)',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <div style={{ fontSize: 13, color: 'var(--fg-2)', display: 'flex', gap: 12, alignItems: 'center' }}>
            {isCompact && (
              <button
                type="button"
                onClick={() => setDrawerOpen((v) => !v)}
                aria-label={drawerOpen ? 'Close navigation' : 'Open navigation'}
                aria-expanded={drawerOpen}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: 6,
                  margin: '0 -6px',
                  cursor: 'pointer',
                  color: 'var(--ink)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <HamburgerIcon />
              </button>
            )}
            {isDemo && (
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--ember-ink)',
                  background: 'var(--ember-soft)',
                  border: '1px solid var(--ember)',
                  padding: '2px 8px',
                  borderRadius: 2,
                }}
              >
                Demo session
              </span>
            )}
            {isAdmin && (
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--ink-deepest)',
                  background: 'var(--ink-soft)',
                  border: '1px solid var(--ink)',
                  padding: '2px 8px',
                  borderRadius: 2,
                }}
              >
                Admin
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 13 }}>
            {user && (
              <span style={{ color: 'var(--fg-2)' }}>
                {user.displayName}
                {user.handle && (
                  <span style={{ color: 'var(--fg-3)', marginLeft: 6 }}>@{user.handle}</span>
                )}
              </span>
            )}
            <button
              type="button"
              onClick={async () => {
                await logout();
                navigate('/');
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--fg-2)',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              Sign out
            </button>
          </div>
        </header>
        <main
          style={{
            padding: isCompact ? '20px 16px 48px' : '32px 32px 64px',
            maxWidth: 1240,
            width: '100%',
          }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}

function SidebarLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      end
      style={({ isActive }) => ({
        display: 'block',
        padding: '8px 12px',
        borderRadius: 4,
        fontSize: 14,
        fontWeight: isActive ? 600 : 500,
        textDecoration: 'none',
        color: isActive ? 'var(--ink)' : 'var(--fg-2)',
        background: isActive ? 'var(--ink-soft)' : 'transparent',
      })}
    >
      {children}
    </NavLink>
  );
}

function HamburgerIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
